import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Stethoscope, Home, Briefcase, GraduationCap, Scale, UtensilsCrossed, Bot, Check } from "lucide-react";

const useCases = [
  {
    id: "ecommerce",
    icon: ShoppingCart,
    title: "E-commerce",
    subtitle: "Lojas e Marketplaces",
    color: "cyber-cyan",
    benefits: [
      "Recuperação de carrinho abandonado automática",
      "Rastreamento de pedidos via WhatsApp",
      "Integração com Shopify, Nuvemshop, Yampi",
      "Disparos em massa para promoções",
      "Atendimento 24/7 com IA treinada",
    ],
    result: "+45% de conversão em 30 dias",
  },
  {
    id: "clinicas",
    icon: Stethoscope,
    title: "Clínicas",
    subtitle: "Saúde e Estética",
    color: "electric-purple",
    benefits: [
      "Agendamentos automáticos 24h",
      "Lembretes de consulta personalizados",
      "Confirmação e remarcação via IA",
      "Filas de espera inteligentes",
      "Integração com sistemas de gestão",
    ],
    result: "-70% de faltas em consultas",
  },
  {
    id: "imobiliarias",
    icon: Home,
    title: "Imobiliárias",
    subtitle: "Incorporadoras e Corretores",
    color: "cyber-cyan",
    benefits: [
      "Qualificação automática de leads interessados em imóveis",
      "Envio de fotos, vídeos e plantas via WhatsApp",
      "Agendamento de visitas com confirmação automática",
      "Follow-up de propostas sem esforço manual",
      "CRM com histórico completo de cada lead",
    ],
    result: "+60% de visitas agendadas em 30 dias",
  },
  {
    id: "agencias",
    icon: Briefcase,
    title: "Agências",
    subtitle: "Marketing e Vendas",
    color: "electric-purple",
    benefits: [
      "Gestão multi-cliente em um só painel",
      "White-label para sua marca",
      "Relatórios por cliente automatizados",
      "Equipes separadas por projeto",
      "Automações personalizadas por nicho",
    ],
    result: "3x mais clientes por atendente",
  },
  {
    id: "educacao",
    icon: GraduationCap,
    title: "Educação",
    subtitle: "Cursos e Infoprodutos",
    color: "cyber-cyan",
    benefits: [
      "Suporte em massa para alunos",
      "Recuperação de boleto automática",
      "Integração com Hotmart, Kiwify, Eduzz",
      "Comunicação automatizada de novos alunos",
      "Notificações de aulas e conteúdos",
    ],
    result: "+60% de retenção de alunos",
  },
  {
    id: "juridico",
    icon: Scale,
    title: "Jurídico",
    subtitle: "Escritórios de Advocacia",
    color: "electric-purple",
    benefits: [
      "Triagem inicial de casos com chatbot especializado",
      "Envio seguro de documentos via WhatsApp com confirmação",
      "Lembretes automáticos de prazos para clientes",
      "Atendimento humanizado fora do horário comercial com IA",
      "Histórico organizado por cliente e processo",
    ],
    result: "-70% de ligações repetitivas",
  },
  {
    id: "restaurantes",
    icon: UtensilsCrossed,
    title: "Restaurantes",
    subtitle: "Food & Beverage",
    color: "cyber-cyan",
    benefits: [
      "Cardápio digital interativo via WhatsApp",
      "Recebimento e confirmação de pedidos automaticamente",
      "Programa de fidelidade via mensagem",
      "Disparo de promoções segmentadas (happy hour, fim de semana)",
      "Integração com sistemas de delivery",
    ],
    result: "+35% de pedidos recorrentes",
  },
  {
    id: "assistentes",
    icon: Bot,
    title: "Assistentes Virtuais",
    subtitle: "Automação e IA",
    color: "electric-purple",
    benefits: [
      "Criação de assistentes personalizados",
      "Integração com bases de conhecimento",
      "Fluxos conversacionais avançados",
      "Respostas humanizadas com IA",
      "Escalabilidade sem limite de conversas",
    ],
    result: "90% de atendimentos automatizados",
  },
];

const UseCasesSection = () => {
  const [activeTab, setActiveTab] = useState("ecommerce");
  const activeCase = useCases.find((uc) => uc.id === activeTab);

  return (
    <section id="casos-de-uso" className="py-16 md:py-24 relative" aria-labelledby="use-cases-heading">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface-card/30 to-transparent" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <h2 id="use-cases-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            Feito para o{" "}
            <span className="text-gradient-zavvi">seu segmento</span>
          </h2>
          <p className="text-base md:text-lg text-body-text">
            Soluções prontas para o seu nicho — sem configuração complexa
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-8 md:mb-12">
          {useCases.map((useCase) => (
            <motion.button
              key={useCase.id}
              onClick={() => setActiveTab(useCase.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full text-sm md:text-base font-medium transition-all duration-300 ${
                activeTab === useCase.id
                  ? `bg-${useCase.color}/20 text-${useCase.color} neon-border-${useCase.color === "cyber-cyan" ? "cyan" : "purple"}`
                  : "glass-card text-muted-foreground hover:text-foreground"
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <useCase.icon size={18} />
              <span className="hidden sm:inline">{useCase.title}</span>
              <span className="sm:hidden">{useCase.title}</span>
            </motion.button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeCase && (
            <motion.div
              key={activeCase.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <div className="glass-card rounded-2xl md:rounded-3xl p-6 md:p-10">
                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                  {/* Left - Info */}
                  <div>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-${activeCase.color}/10 text-${activeCase.color} text-sm font-medium mb-4`}>
                      <activeCase.icon size={16} />
                      {activeCase.subtitle}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-display font-bold mb-4 text-foreground">
                      Zavvi para {activeCase.title}
                    </h3>
                    
                    {/* Benefits */}
                    <ul className="space-y-3">
                      {activeCase.benefits.map((benefit, index) => (
                        <motion.li
                          key={benefit}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <div className={`w-5 h-5 rounded-full bg-${activeCase.color}/20 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <Check size={12} className={`text-${activeCase.color}`} />
                          </div>
                          <span className="text-sm md:text-base text-body-text">{benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Right - Result */}
                  <div className="flex items-center justify-center">
                    <div className={`text-center p-6 md:p-8 rounded-2xl bg-${activeCase.color}/5 border border-${activeCase.color}/20`}>
                      <div className="text-xs md:text-sm text-muted-foreground mb-2 uppercase tracking-wide">
                        Resultado Médio
                      </div>
                      <div className={`text-3xl md:text-4xl lg:text-5xl font-display font-bold text-${activeCase.color}`}>
                        {activeCase.result}
                      </div>
                      <div className="text-xs md:text-sm text-muted-foreground mt-2">
                        com clientes Zavvi
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default UseCasesSection;
